import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import "./Notifications.css";
import ErrorComponent from "../components/Error";
import { notificationService } from "../utils/api";
import Modal from "../components/Modal";
import Menu, { type MenuItem } from "../components/Menu";
import { useUser } from "../contexts/UserContext";
import { useMultiLot } from "../contexts/MultiLotContext";
import { NotificationItem, NotificationWithLot, PaginationParams, PageResponse } from "../types";
import { usePagination } from "../hooks/usePagination";
import Pagination from "../components/Pagination";
import {
  isSuperadminModeEnabled,
  SUPERADMIN_MODE_EVENT,
  SUPERADMIN_MODE_STORAGE_KEY,
} from "../components/SuperadminHeader";

interface DisplayNotification extends NotificationItem {
  id: string;
  isSelected: boolean;
  date: string; // For compatibility with existing code
  type: string; // For compatibility with existing code
  lotName?: string; // For multi-lot mode
}

const DEFAULT_PAGE_SIZE = 10; // Default page size for notifications data
const NOTIFICATION_TYPES = ["ALERT", "ERROR", "VIOLATION", "MAINTENANCE", "OCCUPANCY"] as const;
type NotificationTypeValue = typeof NOTIFICATION_TYPES[number];

interface CreateNotificationFormState {
  title: string;
  message: string;
  location: string;
  notificationTimestamp: string;
  notificationType: NotificationTypeValue;
}

const formatLocalDateToDatetimeLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const Notifications: React.FC = () => {
  const { lotId } = useParams<{ lotId: string }>();
  const { user } = useUser();
  const { isMultiLotMode, getSelectedLotIds, getSelectedLotNames } = useMultiLot();
  const selectedLotIds = isMultiLotMode ? getSelectedLotIds() : [lotId || ''];
  const lotNameMap = getSelectedLotNames();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paginatedNotifications, setPaginatedNotifications] = useState<DisplayNotification[]>([]);
  const [multiLotNotifications, setMultiLotNotifications] = useState<DisplayNotification[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isShowRecipientsDisabledModal, setIsShowRecipientsDisabledModal] = useState(false);
  const [isCreateNotificationModalOpen, setIsCreateNotificationModalOpen] = useState(false);
  const [isCreatingNotification, setIsCreatingNotification] = useState(false);
  const [createNotificationError, setCreateNotificationError] = useState<string | null>(null);

  // For the image modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImageSrc, setModalImageSrc] = useState<string | null>(null);
  const [modalNotifId, setModalNotifId] = useState<string | null>(null);

  // Timer ref to mark notifications as read on the server
  const markReadTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Filter state - single selection
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<string>('all'); // 'all', 'violations', 'aiPredictions', 'software', 'general'
  const [isNotificationTypeMenuOpen, setIsNotificationTypeMenuOpen] = useState(false);
  const [isSuperadminModeOn, setIsSuperadminModeOn] = useState<boolean>(() => isSuperadminModeEnabled());

  const [createNotificationForm, setCreateNotificationForm] = useState<CreateNotificationFormState>({
    title: "",
    message: "",
    location: "",
    notificationTimestamp: formatLocalDateToDatetimeLocal(new Date()),
    notificationType: "ALERT",
  });

  const accountRole = (user?.role || "").toLowerCase();
  const isAccountSuperadmin = accountRole === "superadmin";
  const canCreateNotifications = isAccountSuperadmin && isSuperadminModeOn;

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handleModeChange = (event: Event) => {
      const detail = (event as CustomEvent<{ enabled: boolean }>).detail;
      setIsSuperadminModeOn(detail.enabled);
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key === SUPERADMIN_MODE_STORAGE_KEY) {
        setIsSuperadminModeOn(event.newValue === "true");
      }
    };

    window.addEventListener(SUPERADMIN_MODE_EVENT, handleModeChange as EventListener);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(SUPERADMIN_MODE_EVENT, handleModeChange as EventListener);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const queueMarkRead = useCallback((unread: DisplayNotification[]) => {
    if (markReadTimerRef.current) {
      clearTimeout(markReadTimerRef.current);
    }
    if (unread.length === 0) {
      return;
    }
    markReadTimerRef.current = setTimeout(() => {
      Promise.all(
        unread.map((n: DisplayNotification) =>
          notificationService.markAsRead(n.lotId, n.notificationId)
        )
      ).catch(error => console.error('Error marking notifications as read:', error));
    }, 4000);
  }, []);

  const notificationFetchFn = useCallback(
    async (params: PaginationParams): Promise<PageResponse<NotificationWithLot>> => {
      if (!lotId) {
        return {
          content: [],
          page: 0,
          size: params.size,
          totalElements: 0,
          totalPages: 0,
          first: true,
          last: true,
        };
      }

      const response = await notificationService.getNotificationsByLotOffsetPaginated(lotId, params);
      const currentTime = new Date();
      const twentyFourHoursAgo = new Date(currentTime.getTime() - 24 * 60 * 60 * 1000);

      const filteredContent = response.content.filter((n: NotificationItem) =>
        !(n.notificationType === "OCCUPANCY" &&
          n.title === "Average occupancy expected" &&
          new Date(n.notificationTimestamp) < twentyFourHoursAgo)
      );

      return {
        ...response,
        content: filteredContent.map((n: NotificationWithLot) => ({
          ...n,
          lotName: lotNameMap.get(n.lotId) || "Unknown Lot",
        })),
      };
    },
    [lotId, lotNameMap]
  );

  const {
    items: paginatedItems,
    totalElements: paginatedTotalElements,
    totalPages: paginatedTotalPages,
    page,
    loading: paginationLoading,
    error: paginationError,
    goToPage,
    refresh: refreshPagination,
  } = usePagination<NotificationWithLot>({
    fetchFn: notificationFetchFn,
    initialPage: 0,
    initialSize: DEFAULT_PAGE_SIZE,
    initialSortBy: "notificationTimestamp",
    initialSortDirection: "desc",
    enabled: !!lotId && !isMultiLotMode,
    resetKey: lotId,
  });

  useEffect(() => {
    if (isMultiLotMode) return;
    const mapped = paginatedItems.map((n) => ({
      ...n,
      id: `notif_${n.notificationId}`,
      isSelected: selectedIds.has(n.notificationId),
      date: n.notificationTimestamp,
      type: n.notificationType,
    }));
    setPaginatedNotifications(mapped);
    setIsLoading(paginationLoading);
    if (paginationError) {
      setError(paginationError);
    } else {
      setError(null);
    }
  }, [paginatedItems, isMultiLotMode, selectedIds, paginationLoading, paginationError]);

  interface FetchNotificationsOptions {
    skipMarkRead?: boolean;
  }

  const fetchMultiLotNotifications = useCallback(
    async (options: FetchNotificationsOptions = {}) => {
      if (!isMultiLotMode) return;
      const { skipMarkRead = false } = options;
      setIsLoading(true);
      try {
        const params: PaginationParams = {
          page: 0,
          size: DEFAULT_PAGE_SIZE,
          sortBy: "notificationTimestamp",
          sortDirection: "desc",
        };
        const responses = await Promise.all(
          selectedLotIds.map(lid =>
            notificationService.getNotificationsByLotOffsetPaginated(lid, params)
          )
        );

        const currentTime = new Date();
        const twentyFourHoursAgo = new Date(currentTime.getTime() - 24 * 60 * 60 * 1000);
        const allNotificationsData: DisplayNotification[] = [];

        responses.forEach((data, index) => {
          const lid = selectedLotIds[index];
          const lotName = lotNameMap.get(lid) || 'Unknown Lot';
          const filteredData = data.content.filter((n: NotificationItem) =>
            !(n.notificationType === "OCCUPANCY" &&
              n.title === "Average occupancy expected" &&
              new Date(n.notificationTimestamp) < twentyFourHoursAgo)
          );

          filteredData.forEach((n: NotificationItem) => {
            allNotificationsData.push({
              ...n,
              id: `notif_${n.notificationId}`,
              isSelected: selectedIds.has(n.notificationId),
              date: n.notificationTimestamp,
              type: n.notificationType,
              lotName,
            });
          });
        });

        allNotificationsData.sort((a, b) =>
          new Date(b.notificationTimestamp).getTime() - new Date(a.notificationTimestamp).getTime()
        );

        setMultiLotNotifications(allNotificationsData);
        setError(null);

        if (!skipMarkRead) {
          queueMarkRead(allNotificationsData.filter((n) => !n.read));
        }
      } catch (fetchError) {
        console.error('Error fetching notifications:', fetchError);
        setError('Failed to load notifications. Please try again.');
        setMultiLotNotifications([]);
      } finally {
        setIsLoading(false);
      }
    },
    [isMultiLotMode, selectedLotIds, lotNameMap, queueMarkRead, selectedIds]
  );

  useEffect(() => {
    if (isMultiLotMode) {
      fetchMultiLotNotifications();
    }

    return () => {
      if (markReadTimerRef.current) clearTimeout(markReadTimerRef.current);
    };
  }, [lotId, isMultiLotMode, selectedLotIds.join(','), fetchMultiLotNotifications]);

  useEffect(() => {
    setPaginatedNotifications((prev) =>
      prev.map((item) => ({
        ...item,
        isSelected: selectedIds.has(item.notificationId),
      }))
    );
    setMultiLotNotifications((prev) =>
      prev.map((item) => ({
        ...item,
        isSelected: selectedIds.has(item.notificationId),
      }))
    );
  }, [selectedIds]);

  useEffect(() => {
    if (!isMultiLotMode) {
      queueMarkRead(paginatedNotifications.filter((n) => !n.read));
    } else {
      queueMarkRead(multiLotNotifications.filter((n) => !n.read));
    }
  }, [paginatedNotifications, multiLotNotifications, isMultiLotMode, queueMarkRead]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [lotId, isMultiLotMode, selectedLotIds.join(",")]);

  const notifications = isMultiLotMode ? multiLotNotifications : paginatedNotifications;
  const selectedCount = useMemo(
    () => notifications.filter((n) => selectedIds.has(n.notificationId)).length,
    [notifications, selectedIds]
  );

  const toggleSelect = (notificationId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(notificationId)) {
        next.delete(notificationId);
      } else {
        next.add(notificationId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    const next = new Set(selectedIds);
    notifications.forEach((item) => next.add(item.notificationId));
    setSelectedIds(next);
  };

  const handleUnselectAll = () => {
    const next = new Set(selectedIds);
    notifications.forEach((item) => next.delete(item.notificationId));
    setSelectedIds(next);
  };

  const handleDelete = async () => {
    if (selectedCount === 0) return;

    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    const selectedNotifs = notifications.filter(item => selectedIds.has(item.notificationId));

    if (selectedNotifs.length === 0) return;

    try {
      await Promise.all(
        selectedNotifs.map(item =>
          notificationService.softDelete(item.lotId, item.notificationId)
        )
      );

      const idsToRemove = new Set(selectedNotifs.map(item => item.notificationId));
      setPaginatedNotifications(prev => prev.filter(item => !idsToRemove.has(item.notificationId)));
      setMultiLotNotifications(prev => prev.filter(item => !idsToRemove.has(item.notificationId)));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        idsToRemove.forEach((id) => next.delete(id));
        return next;
      });
      setShowDeleteModal(false);
    } catch (error) {
      console.error('Error deleting notifications:', error);
      setShowDeleteModal(false);
    }
  };

  const handleMarkAsRead = async () => {
    const selectedUnreadNotifs = notifications
      .filter(item => selectedIds.has(item.notificationId) && !item.read);

    if (selectedUnreadNotifs.length === 0) return;

    try {
      await Promise.all(
        selectedUnreadNotifs.map(item =>
          notificationService.markAsRead(item.lotId, item.notificationId)
        )
      );

      const selectedUnreadIds = new Set(selectedUnreadNotifs.map(item => item.notificationId));
      setPaginatedNotifications(prev =>
        prev.map(item =>
          selectedUnreadIds.has(item.notificationId) ? { ...item, read: true } : item
        )
      );
      setMultiLotNotifications(prev =>
        prev.map(item =>
          selectedUnreadIds.has(item.notificationId) ? { ...item, read: true } : item
        )
      );
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }
  };

  const handleMarkAsUnread = async () => {
    const selectedReadNotifs = notifications
      .filter(item => selectedIds.has(item.notificationId) && item.read);

    if (selectedReadNotifs.length === 0) return;

    try {
      await Promise.all(
        selectedReadNotifs.map(item =>
          notificationService.markAsUnread(item.lotId, item.notificationId)
        )
      );

      const selectedReadIds = new Set(selectedReadNotifs.map(item => item.notificationId));
      setPaginatedNotifications(prev =>
        prev.map(item =>
          selectedReadIds.has(item.notificationId) ? { ...item, read: false } : item
        )
      );
      setMultiLotNotifications(prev =>
        prev.map(item =>
          selectedReadIds.has(item.notificationId) ? { ...item, read: false } : item
        )
      );
    } catch (error) {
      console.error('Error marking notifications as unread:', error);
    }
  };

  const filterNotifications = (items: DisplayNotification[]) => {
    if (selectedFilter === 'all') return items;

    return items.filter(item => {
      const type = item.type;

      if (selectedFilter === 'violations' && type === "VIOLATION") return true;
      if (selectedFilter === 'aiPredictions' && type === "OCCUPANCY") return true;
      if (selectedFilter === 'software' && type === "Software") return true;
      if (selectedFilter === 'general' && type !== "VIOLATION" && type !== "OCCUPANCY" && type !== "Software") return true;

      return false;
    });
  };

  const sortNotifications = (items: DisplayNotification[]) => {
    return items.sort((a, b) => {
      // Priority 1: Unread notifications come before read notifications
      if (!a.read && b.read) return -1;
      if (a.read && !b.read) return 1;

      // Priority 2: Among unread notifications, non-violation notifications come before violation notifications
      if (!a.read && !b.read) {
        const aIsViolation = a.type === "VIOLATION";
        const bIsViolation = b.type === "VIOLATION";

        if (!aIsViolation && bIsViolation) return -1;
        if (aIsViolation && !bIsViolation) return 1;
      }

      // Priority 3: Among read notifications, sort by date (newer first)
      if (a.read && b.read) {
        return new Date(b.date).getTime() - new Date(a.date).getTime();
      }

      // Priority 4: If both are unread and have same violation status, sort by date (newer first)
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });
  };

  const filteredAndSorted = useMemo(
    () => sortNotifications(filterNotifications([...notifications])),
    [notifications, selectedFilter]
  );
  const visible = filteredAndSorted;

  const handlePageChange = (nextPage: number) => {
    if (isMultiLotMode) return;
    goToPage(nextPage);
  };

  const handleNotificationButton = (notif: DisplayNotification) => {
    if (notif.type === "Software") {
      alert("Software update not available.");
      return;
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setModalImageSrc(null);
    setModalNotifId(null);
  };

  const handleDeleteFromModal = async () => {
    if (modalNotifId) {
      try {
        const notif = notifications.find(item => item.notificationId === modalNotifId);
        const targetLotId = notif?.lotId || lotId || '';
        await notificationService.softDelete(targetLotId, modalNotifId);
        setPaginatedNotifications((prev) => prev.filter((item) => item.notificationId !== modalNotifId));
        setMultiLotNotifications((prev) => prev.filter((item) => item.notificationId !== modalNotifId));
        setSelectedIds((prev) => {
          const next = new Set(prev);
          next.delete(modalNotifId);
          return next;
        });
        handleCloseModal();
      } catch (error) {
        console.error('Error deleting notification:', error);
      }
    }
  };

  const selectFilter = (filter: string) => {
    setSelectedFilter(filter);
  };

  const filterOptions = useMemo(() => (
    [
      { id: 'all', label: 'All' },
      { id: 'violations', label: 'Violations' },
      { id: 'aiPredictions', label: 'AI (Beta)' },
      { id: 'software', label: 'Software' },
      { id: 'general', label: 'General' },
    ]
  ), []);

  const filterMenuItems: MenuItem[] = useMemo(() => (
    filterOptions.map((option) => ({
      key: option.id,
      label: option.label,
      selected: selectedFilter === option.id,
      onSelect: () => selectFilter(option.id),
    }))
  ), [filterOptions, selectedFilter]);

  const notificationTypeMenuItems: MenuItem[] = useMemo(() => (
    NOTIFICATION_TYPES.map((type) => ({
      key: type,
      label: type.charAt(0) + type.slice(1).toLowerCase(),
      selected: createNotificationForm.notificationType === type,
      onSelect: () => setCreateNotificationForm((prev) => ({ ...prev, notificationType: type })),
    }))
  ), [createNotificationForm.notificationType]);

  const closeCreateNotificationModal = () => {
    if (isCreatingNotification) return;
    setIsCreateNotificationModalOpen(false);
    setCreateNotificationError(null);
  };

  const openCreateNotificationModal = () => {
    setCreateNotificationError(null);
    setIsCreateNotificationModalOpen(true);
  };

  const resetCreateNotificationForm = () => {
    setCreateNotificationForm({
      title: "",
      message: "",
      location: "",
      notificationTimestamp: formatLocalDateToDatetimeLocal(new Date()),
      notificationType: "ALERT",
    });
  };

  const handleCreateNotificationSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!lotId) {
      setCreateNotificationError("Missing lot information. Please refresh and try again.");
      return;
    }

    try {
      setIsCreatingNotification(true);
      setCreateNotificationError(null);

      const payload = {
        ...createNotificationForm,
        lotId,
        read: false,
        visibleBy: ["Operator"],
        createdBy: "SuperAdmin",
        notificationTimestamp: new Date(createNotificationForm.notificationTimestamp).toISOString(),
      };

      const response = await notificationService.createNotification(payload);

      if (!response?.success) {
        throw new Error(response?.message || "Unable to create notification.");
      }

      resetCreateNotificationForm();
      setIsCreateNotificationModalOpen(false);
      if (isMultiLotMode) {
        await fetchMultiLotNotifications({ skipMarkRead: true });
      } else {
        refreshPagination();
      }
    } catch (submitError: any) {
      const message = submitError?.response?.data?.message || submitError?.message || "Failed to create notification.";
      setCreateNotificationError(message);
      console.error("Error creating notification:", submitError);
    } finally {
      setIsCreatingNotification(false);
    }
  };

  const loading = isMultiLotMode ? isLoading : paginationLoading;
  const totalElements = isMultiLotMode ? notifications.length : paginatedTotalElements;
  const totalPages = isMultiLotMode ? 1 : paginatedTotalPages;
  const currentPage = isMultiLotMode ? 0 : page;

  if (loading) {
    return (
      <div className="notifications-mobile-wrapper">
        <div className="sticky-header">
          <div className="notifications-header">
            <h1>Notification Manager</h1>
          </div>
        </div>

        <div className="table-container notifications-table-skeleton">
          <div className="skeleton-block notifications-table-skeleton-block" aria-hidden="true" />
        </div>
      </div>
    );
  }

  if (error) {
    return <ErrorComponent message={error} />;
  }

  return (
    <div className="notifications-mobile-wrapper">
      <div className="sticky-header">
        <div className="notifications-header">
          <h1>Notification Manager</h1>
          {canCreateNotifications && (
            <button
              type="button"
              className="button-secondary create-notification-btn"
              onClick={openCreateNotificationModal}
            >
              Create Notification
            </button>
          )}
        </div>
      </div>

      <div className="table-container">
        <table className={`notifications-table app-table app-table-secondary-hover ${totalElements === 0 ? 'empty' : ''}`}>
          <thead>
            <tr>
              <th colSpan={3}>
                <div className="notif-actions">
                  {selectedCount > 0 ? (
                    <>
                      <button className="notif-button" onClick={handleUnselectAll}><img src="/assets/notifications/unselect all.svg" alt="Unselect All" className="button-icon" /><span className="mobile-text">Unsel</span><span className="desktop-text">Unselect All</span></button>
                      {selectedCount < visible.length && <button className="notif-button" onClick={handleSelectAll}><img src="/assets/notifications/select all.svg" alt="Select All" className="button-icon" /><span className="mobile-text">All</span><span className="desktop-text">Select All</span></button>}
                      <button className="notif-button" onClick={handleDelete}><img src="/assets/notifications/dismiss.svg" alt="Delete" className="button-icon" />Delete</button>
                      {notifications.filter(n => selectedIds.has(n.notificationId)).some(n => !n.read) ? <button className="notif-button" onClick={handleMarkAsRead}><img src="/assets/notifications/markasread.svg" alt="Mark as Read" className="button-icon" /><span className="mobile-text">Read</span><span className="desktop-text">Mark as Read</span></button> : null}
                      {notifications.filter(n => selectedIds.has(n.notificationId)).some(n => n.read) ? <button className="notif-button" onClick={handleMarkAsUnread}><img src="/assets/notifications/markasunread.svg" alt="Mark as Unread" className="button-icon" /><span className="mobile-text">New</span><span className="desktop-text">Mark as Unread</span></button> : null}
                    </>
                  ) : (
                    <button className="notif-button" onClick={handleSelectAll}><img src="/assets/notifications/select all.svg" alt="Select All" className="button-icon" /><span className="mobile-text">Sel</span><span className="desktop-text">Select All</span></button>
                  )}
                  {/* <button className="recipients-btn" onClick={() => setIsShowRecipientsDisabledModal(true)}>
                    <img src="/assets/AddPerson.svg" alt="Add Recipients" className="icon-person" />
                    Add Recipients
                  </button> */}
                  <div className="filter-container">
                    <div className="menu-wrapper">
                      <Menu
                        trigger={
                          <button className="filter-btn" type="button">
                            <img src="/assets/notifications/filter.svg" alt="Filter" className="icon-filter" />
                            Filter
                            <img
                              src="/assets/ArrowDropdown.svg"
                              alt="Toggle filter menu"
                              className={`filter-arrow ${isFilterMenuOpen ? 'open' : ''}`}
                            />
                          </button>
                        }
                        items={filterMenuItems}
                        placement="bottom-end"
                        dropdownClassName="filter-dropdown-menu"
                        onOpenChange={setIsFilterMenuOpen}
                        allowNoSelection={false}
                      />
                    </div>
                  </div>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {visible.map((item: DisplayNotification) => {
              let buttonLabel = "";
              if (item.type === "Software") {
                buttonLabel = "Update";
              }

              return (
                <tr key={item.notificationId}
                  onClick={() => toggleSelect(item.notificationId)}
                >
                  <td className="checkbox-col">
                    <label className="custom-checkbox">
                      <input
                        type="checkbox"
                        className="hidden-checkbox"
                        checked={item.isSelected}
                        onChange={() => toggleSelect(item.notificationId)}
                      />
                      <span className="checkbox-styler"></span>
                    </label>
                  </td>

                  <td className="notif-info-col">
                    <div className="notif-title-row">
                      <span className="notif-title">{item.title}</span>
                      {!item.read && <span className="new-badge">NEW</span>}
                      {item.type === "VIOLATION" && <span className="violation-badge">Violation</span>}
                      {item.type === "OCCUPANCY" && <span className="ai-badge"><span className="mobile-text">AI</span><span className="desktop-text">AI PREDICTION</span></span>}
                      {item.type === "OCCUPANCY" && <span className="beta-badge">BETA</span>}
                      <span className="notif-date-loc">
                        {new Date(item.date).toLocaleString("en-US", {
                          weekday: "short",
                          month: "long",
                          day: "numeric",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                        {isMultiLotMode && item.lotName && ` - ${item.lotName}`}
                      </span>
                    </div>
                    <div className="notif-message">{item.message}</div>
                  </td>

                  <td className="button-col">
                    {buttonLabel && (
                      <button className="link-btn" onClick={() => handleNotificationButton(item)}>
                        {buttonLabel}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Table Footer */}
      {totalElements > 0 ? (
        <Pagination
          currentPage={currentPage}
          totalPages={Math.max(totalPages, 1)}
          totalItems={totalElements}
          itemsOnPage={visible.length}
          onPageChange={handlePageChange}
          disabled={isMultiLotMode}
        />
      ) : (
        <div className="empty-state-message">
          No notifications yet.
        </div>
      )}

      {/* Image modal */}
      {modalOpen && modalImageSrc && (
        <div className="modal-overlay">
          <div className="modal-notification-content">
            <h2>
              {visible.find((v) => v.id === modalNotifId)?.title || "Notification"}
            </h2>
            <p>
              {visible.find((v) => v.id === modalNotifId)?.message || "No message."}
            </p>
            <img src={modalImageSrc} alt="No Image Found" className="modal-image" />
            <div className="modal-buttons">
              <button className="delete-btn" onClick={handleDeleteFromModal}>
                Delete Notice
              </button>
              <button className="close-btn" onClick={handleCloseModal}>
                Close Image
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        title="Delete Notification"
        description="Notifications that are deleted are no longer recoverable."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => setShowDeleteModal(false)}
      />
      <Modal
        isOpen={isShowRecipientsDisabledModal}
        title="Sorry, this feature is disabled."
        description="This feature allows you to add recipients for the notifications to be sent over email or text. Activation of this feature may come at an added cost."
        confirmText=""
        cancelText="Return"
        onConfirm={() => { }}
        onCancel={() => setIsShowRecipientsDisabledModal(false)}
        disableConfirm={true}
      />
      <Modal
        isOpen={isCreateNotificationModalOpen}
        title="Create Notification"
        onCancel={closeCreateNotificationModal}
        hideButtons
        customContent={
          <form className="notification-create-form" onSubmit={handleCreateNotificationSubmit}>
            <div className="form-field">
              <label htmlFor="notification-title">Title</label>
              <input
                id="notification-title"
                type="text"
                value={createNotificationForm.title}
                onChange={(e) => setCreateNotificationForm((prev) => ({ ...prev, title: e.target.value }))}
                required
                disabled={isCreatingNotification}
              />
            </div>

            <div className="form-field">
              <label htmlFor="notification-message">Message</label>
              <textarea
                id="notification-message"
                value={createNotificationForm.message}
                onChange={(e) => setCreateNotificationForm((prev) => ({ ...prev, message: e.target.value }))}
                required
                disabled={isCreatingNotification}
                rows={4}
              />
            </div>

            <div className="notification-form-row">
              <div className="form-field">
                <label htmlFor="notification-location">Location</label>
                <input
                  id="notification-location"
                  type="text"
                  value={createNotificationForm.location}
                  onChange={(e) => setCreateNotificationForm((prev) => ({ ...prev, location: e.target.value }))}
                  required
                  disabled={isCreatingNotification}
                />
              </div>
              <div className="form-field">
                <label htmlFor="notification-timestamp">Timestamp</label>
                <input
                  id="notification-timestamp"
                  type="datetime-local"
                  value={createNotificationForm.notificationTimestamp}
                  onChange={(e) => setCreateNotificationForm((prev) => ({ ...prev, notificationTimestamp: e.target.value }))}
                  required
                  disabled={isCreatingNotification}
                />
              </div>
            </div>

            <div className="form-field">
              <label>Notification Type</label>
              <div className="menu-wrapper">
                <Menu
                  trigger={
                    <button
                      type="button"
                      className="dropdown-input"
                    >
                      <span>{createNotificationForm.notificationType.charAt(0) + createNotificationForm.notificationType.slice(1).toLowerCase()}</span>
                      <img
                        src="/assets/ArrowDropdown.svg"
                        alt=""
                        className={`filter-arrow ${isNotificationTypeMenuOpen ? "open" : ""}`}
                      />
                    </button>
                  }
                  items={notificationTypeMenuItems}
                  placement="bottom-start"
                  dropdownClassName="filter-dropdown-menu"
                  allowNoSelection={false}
                  onOpenChange={setIsNotificationTypeMenuOpen}
                />
              </div>
            </div>

            {createNotificationError && (
              <p className="notification-form-error">{createNotificationError}</p>
            )}

            <div className="modal-actions form-actions-inline">
              <button
                type="submit"
                className="button-primary"
                disabled={isCreatingNotification}
              >
                {isCreatingNotification ? "Creating..." : "Create Notification"}
              </button>
              <button
                type="button"
                className="button-secondary"
                onClick={closeCreateNotificationModal}
                disabled={isCreatingNotification}
              >
                Cancel
              </button>
            </div>
          </form>
        }
      />
    </div>
  );
};

export default Notifications;
